import { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { Button } from '@/components/ui/button';
import { cn, objectToFormData, validateStep } from '@/lib/utils';
import ModelUpload from './Carousel/ModelUpload';
import CarouselHeader from '@/components/FullPageCarousel/CarouselHeader';
import MediaUpload from './Carousel/MediaUpload';
import PostStatus from './Carousel/PostStatus';
import BasicDetails from './Carousel/BasicDetails';
import {
  useCreateDraftPostMutation,
  useLazyGetPostByIdQuery,
  useUpdatePostAttachmentMutation,
  useUpdatePostDataMutation,
} from '@/redux/post/post.api.slice';
import {
  jobSetupModelSchema,
  licenceDetailsSchema,
  mediaUploadSchema,
  modelToolMappingPageSchema,
  modelUploadSchema,
  postUploadSchema,
} from '@/redux/post/post.schema';
import {
  GroupedEnum,
  IJobType,
  ILaserOrEndmill,
  ILicense,
  IMaterialUsed,
  IPostDraft,
  IPostUploadData,
  IProbeForZHeightCommand,
  ISoftwareUsed,
  IToolSetter,
  IUnits,
  IXYDatumPosition,
  IZZeroPosition,
} from '@/redux/post/post.d';
import JobSetupDetails from './Carousel/JobSetupDetails';
import {
  getGroupedOptionsFlat,
  convertToPostDetailAttachment,
  convertToPostDetailData,
  convertToPostUploadData,
} from '@/redux/post/post.utils';
import ModelToolMapping from './Carousel/ModelToolMapping';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import Loader from './Carousel/Loader';
import SupportedMachines from './Carousel/SupportedMachines/SupportedMachines';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import LicenceDetails from './Carousel/LicenceDetails';
import { ICarouselPage } from '@/interfaces/types';

export default function PostUpload() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [createDraftPost, { isLoading: isCreateDraftPostLoading }] =
    useCreateDraftPostMutation();
  const [
    updatePostData,
    { isLoading: isUpdatePostDataLoading, status: updatePostDataStatus },
  ] = useUpdatePostDataMutation();
  const [
    updatePostAttachment,
    {
      isLoading: isUpdatePostAttachmentLoading,
      status: updatePostAttachmentStatus,
    },
  ] = useUpdatePostAttachmentMutation();
  const [getPostByIdQuery, { isLoading: isGetPostByIdLoading }] =
    useLazyGetPostByIdQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const { getSearchParam, appendSearchParam } = useSearchParamActions();
  const [formData, setFormData] = useState<IPostUploadData>({
    models: [],
    mediaFiles: [],
    thumbnail: null,
    title: '',
    description: '',
    category: '',
    tags: [],
    onefinityModel: '',
    onefinityController: '',
    onefinitySize: '',
    originalMachine: '',
    supportedMachines: [],
    softwareUsed: Object.values(ISoftwareUsed)[0],
    materialUsed: Object.values(
      getGroupedOptionsFlat(IMaterialUsed as unknown as GroupedEnum)
    )[0] as any,
    jobType: Object.values(IJobType)[0],
    units: Object.values(IUnits)[0],
    jobSizeHeight: 0.1,
    jobSizeThickness: 0.1,
    jobSizeWidth: 0.1,
    jobSizeDiameter: 0.1,
    jobSizeLength: 0.1,
    zZeroPosition: Object.values(IZZeroPosition)[0],
    xyDatumPosition: Object.values(IXYDatumPosition)[0],
    laserOrEndmill: Object.values(ILaserOrEndmill)[0],
    toolData: [],
    toolSetter: Object.values(IToolSetter)[0],
    probeForZHeightCommand: Object.values(IProbeForZHeightCommand)[0],
    spindleSpeed: 6000,
    youtubeAttachments: [],
    licenceType: ILicense.CreativeCommonsAttribution,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  async function handleCreateDraftPost(data: Partial<IPostDraft>) {
    try {
      const postFormData = objectToFormData(data);
      const createDraftPostRes = await createDraftPost(postFormData).unwrap();
      navigate(`/project/edit/${createDraftPostRes._id}?page=2`);
      toast.success('Model Post Upload successful');
    } catch (error: any) {
      toast.error('Project creation failed', {
        description: error?.data?.errorMessage || 'Something went wrong',
      });
    }
  }

  const pages = [
    {
      page: (
        <BasicDetails
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          key="basic-details"
        />
      ),
      schema: postUploadSchema,
      condition: true,
      title: 'Basic Details',
    },
    {
      page: (
        <SupportedMachines
          formData={formData}
          setFormData={setFormData}
          key="supported-machines"
        />
      ),
      condition: true,
      title: 'Supported Machines',
    },
    {
      page: (
        <ModelUpload
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          key="model-upload"
        />
      ),
      schema: modelUploadSchema,
      condition: true,
      title: 'Model Upload',
    },
    {
      page: (
        <ModelToolMapping
          formData={formData}
          pageErrors={errors}
          setFormData={setFormData}
          key="onefinity-details"
        />
      ),
      schema: modelToolMappingPageSchema,
      condition: true,
      title: 'Model Tool Mapping',
    },
    {
      page: (
        <MediaUpload
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          key="media-upload"
        />
      ),
      schema: mediaUploadSchema,
      condition: true,
      title: 'Media Upload',
    },
    {
      page: (
        <JobSetupDetails
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          key="job-setup-details"
        />
      ),
      schema: jobSetupModelSchema,
      condition: true,
      title: 'Job Setup Details',
    },
    {
      page: (
        <LicenceDetails
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          key="licence-details"
        />
      ),
      schema: licenceDetailsSchema,
      condition: true,
      title: 'License Details',
    },
    {
      page: (
        <PostStatus key="post-status" isLoading={isCreateDraftPostLoading} />
      ),
    },
  ];

  const handleNext = async () => {
    const nextPage = pages[currentStep];
    const validationResult = await validateStep(
      currentStep,
      pages as ICarouselPage[],
      formData,
      setErrors
    );
    if (validationResult.success) {
      if (location.pathname === '/project/create') {
        await handleCreateDraftPost({
          title: formData.title,
          description: formData.description,
          category: formData.category,
          tags: formData.tags,
          thumbnail: formData.thumbnail,
        });
      } else if (postId) {
        await updatePostData({
          postId,
          data: {
            ...convertToPostDetailData(formData),
            currentPage: currentStep + 1,
          },
        });

        await updatePostAttachment({
          postId,
          currentPage: currentStep + 1,
          data: objectToFormData(convertToPostDetailAttachment(formData)),
        });
      }
      setErrors({});
      if (nextPage.condition) {
        const pageStep = Math.min(currentStep + 1, pages.length);
        setCurrentStep(pageStep);
        appendSearchParam('page', String(pageStep), true);
      } else {
        const pageStep = Math.min(currentStep + 2, pages.length);
        setCurrentStep(pageStep);
        appendSearchParam('page', String(pageStep), true);
      }
    } else {
      toast.error('Please fix the errors before continuing.');
    }
  };

  const handleBack = () => {
    const previousPage = pages[currentStep - 2];
    if (previousPage.condition) {
      const step = Math.max(currentStep - 1, 1);
      setCurrentStep(step);
      appendSearchParam('page', String(step), true);
    } else {
      const step = Math.max(currentStep - 2, 1);
      setCurrentStep(step);
      appendSearchParam('page', String(step), true);
    }
  };

  useEffect(() => {
    const pageStep = getSearchParam('page');
    if (!pageStep) return;
    setCurrentStep(parseInt(pageStep, 10));
  }, [getSearchParam]);

  const updateFormData = useCallback(async () => {
    if (!postId) return;

    setIsLoading(true);
    const { data } = await getPostByIdQuery(postId);
    const currentPage = getSearchParam('page');
    if (!currentPage) return;
    if (!data) return;

    const postFormData = await convertToPostUploadData(data);
    setFormData((prev) => ({
      ...prev,
      ...postFormData,
    }));
    setIsLoading(false);
  }, [getPostByIdQuery, getSearchParam, postId]);

  useEffect(() => {
    if (!postId) return;
    if (isUpdatePostDataLoading) return;
    if (isUpdatePostAttachmentLoading) return;
    if (updatePostDataStatus !== QueryStatus.fulfilled && currentStep !== 1)
      return;
    if (
      updatePostAttachmentStatus !== QueryStatus.fulfilled &&
      currentStep !== 1
    )
      return;

    updateFormData();
  }, [
    currentStep,
    postId,
    isUpdatePostDataLoading,
    isUpdatePostAttachmentLoading,
    updatePostDataStatus,
    updatePostAttachmentStatus,
    updateFormData,
  ]);

  if (
    isCreateDraftPostLoading ||
    isUpdatePostAttachmentLoading ||
    isUpdatePostDataLoading ||
    isGetPostByIdLoading ||
    isLoading
  ) {
    return <Loader isLoading />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background flex-col pb-[100px]">
      <CarouselHeader disabled={isCreateDraftPostLoading} />
      <div className="flex-grow flex items-center justify-center w-full">
        <div className="w-full max-w-2xl space-y-8 p-3">
          <div className="flex justify-center items-center space-x-2">
            {pages.map((page, index) => (
              <Fragment key={index + 1}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <button
                        type="button"
                        className={cn(
                          'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium cursor-pointer hover:shadow-lg hover:bg-green-600 transition-colors duration-200 ease-in-out',
                          currentStep >= index + 1
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-300 text-gray-700'
                        )}
                        onClick={() => setCurrentStep(index + 1)}
                      >
                        {index + 1}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{page.title}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {index + 1 < pages.length && (
                  <div
                    className={cn(
                      'w-16 h-0.5',
                      currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                    )}
                  />
                )}
              </Fragment>
            ))}
          </div>
          {pages[currentStep - 1].page}
          <div className="flex justify-between mt-8 w-full gap-2">
            {currentStep > 1 && currentStep < pages.length && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="w-full rounded-lg"
              >
                Back
              </Button>
            )}
            {currentStep < pages.length ? (
              <Button className="w-full rounded-lg" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                className="w-full"
                disabled={isCreateDraftPostLoading}
                asChild
              >
                <Link to={`/model/${postId}`}>Done</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
